import React from 'react';
import { IconRefresh } from '@tabler/icons-react';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Box } from 'src/design/atoms/Box';
import { DeleteButton } from 'src/design/atoms/DeleteButton';
import { Group } from 'src/design/atoms/Group';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { Modal } from 'src/design/molecules/Modal';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';
import {
  DesignModelFileKind,
  FILE_KINDS,
  FILE_KIND_LABELS,
} from 'src/routes/Jobs/DesignModels/designModelFiles';
import { deleteDesignModelCopy } from 'src/routes/Jobs/DesignModels/designModelStatus';
import { IReprocessBlock } from 'src/routes/Jobs/DesignModels/reprocessGate';

export interface IDesignModelActionBarProps {
  isCreate: boolean;
  modelName: string | undefined;
  reprocessBlock: IReprocessBlock | null;
  savedFileNames: Partial<Record<DesignModelFileKind, string>>;
  onReprocess: () => void;
  onDelete: () => Promise<void>;
  dirty: boolean;
  onCancel: () => void;
}

export const DesignModelActionBar: React.FC<IDesignModelActionBarProps> = ({
  isCreate,
  modelName,
  reprocessBlock,
  savedFileNames,
  onReprocess,
  onDelete,
  dirty,
  onCancel,
}) => {
  const [reprocessOpen, setReprocessOpen] = React.useState(false);
  const deleteAction = useConfirmableAction(onDelete, true);
  const deleteCopy = deleteDesignModelCopy(modelName);

  const reprocessReason =
    reprocessBlock?.display === 'disabled' ? reprocessBlock.reason : undefined;

  return (
    <Group justify="flex-end">
      {reprocessBlock?.display !== 'hidden' && (
        <Tooltip
          label={reprocessReason}
          disabled={!reprocessReason}
          multiline
          maw={300}
        >
          <Box component="span" display="inline-block">
            <SecondaryButton
              leftSection={<IconRefresh size={16} />}
              loading={reprocessBlock?.display === 'loading'}
              disabled={!!reprocessReason}
              onClick={() => setReprocessOpen(true)}
            >
              Reprocess
            </SecondaryButton>
          </Box>
        </Tooltip>
      )}
      {!isCreate && (
        <DeleteButton onClick={() => deleteAction.requestOrRun()} />
      )}
      {dirty ? (
        <InlineConfirmButton
          variant="light"
          color="dark"
          confirmButtonProps={{ children: 'Discard changes' }}
          cancelButtonProps={{ children: 'Keep editing' }}
          onConfirmClick={onCancel}
        >
          Cancel
        </InlineConfirmButton>
      ) : (
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      )}

      <Modal
        title="Reprocess design model?"
        opened={reprocessOpen}
        onClose={() => setReprocessOpen(false)}
        actions={[
          <BasicButton
            key="reprocess"
            onClick={() => {
              setReprocessOpen(false);
              onReprocess();
            }}
          >
            Reprocess
          </BasicButton>,
        ]}
      >
        <Stack gap="sm">
          <Text size="sm">Processing runs again from the saved files:</Text>
          <Text size="sm" c="bright">
            {FILE_KINDS.map(
              (kind) =>
                `${FILE_KIND_LABELS[kind]} ${savedFileNames[kind] ?? '(none)'}`,
            ).join(' · ')}
          </Text>
          <Text size="sm">
            The current 3D model and line grouping are replaced when it
            finishes. Until then the model is unavailable in the Progress
            viewer. Your grouping overrides are reapplied.
          </Text>
        </Stack>
      </Modal>

      <Modal
        title={deleteCopy.title}
        opened={deleteAction.opened}
        onClose={deleteAction.close}
        actions={[
          <DeleteButton
            key="delete"
            loading={deleteAction.loading}
            onClick={() => deleteAction.confirm()}
          />,
        ]}
      >
        <Text>{deleteCopy.text}</Text>
      </Modal>
    </Group>
  );
};
